import { trackEvent } from 'helpers/Analytics';
import {
  clearRecentlyViewed as clearRecentlyViewedApi,
  createEntry,
  deleteEntry,
  fetchLinkPreview as fetchLinkPreviewApi,
  getBoard,
  getStagePage,
  getRecentlyViewed,
  postRecentlyViewed,
  updateEntry,
  updateEntryStage,
} from 'helpers/api/jobTracker';
import { setErrorMessage } from 'redux/modules/errorMessage';
import { setSuccessMessage } from 'redux/modules/successMessage';

export const STAGES = ['saved', 'applied', 'in_talks', 'proposal_sent', 'won'];

export const STAGE_LABELS = {
  saved: 'Saved',
  applied: 'Applied',
  in_talks: 'In talks',
  proposal_sent: 'Proposal sent',
  won: 'Won',
};

const HIGHLIGHT_MS = 2200;

const emptyOrder = () => ({
  saved: [],
  applied: [],
  in_talks: [],
  proposal_sent: [],
  won: [],
});

const emptyCounts = () => ({
  saved: 0,
  applied: 0,
  in_talks: 0,
  proposal_sent: 0,
  won: 0,
});

const emptyPagination = () =>
  STAGES.reduce((next, stage) => {
    next[stage] = {
      hasMore: false,
      nextCursor: null,
      status: 'idle',
    };
    return next;
  }, {});

export const initialState = {
  entries: {},
  order: emptyOrder(),
  counts: emptyCounts(),
  pagination: emptyPagination(),
  status: 'idle',
  error: null,
  filters: {
    source: 'all',
    query: '',
  },
  recentlyViewed: {
    items: [],
    status: 'idle',
    open: false,
  },
  addJob: {
    open: false,
    preview: null,
    previewStatus: 'idle',
    previewRequestId: 0,
    submitting: false,
  },
  editEntry: {
    entryId: null,
  },
  removeEntry: {
    entryId: null,
  },
  highlightedEntryId: null,
  mobile: {
    activeStage: 'saved',
    moveSheetEntryId: null,
  },
};

const FETCH_BOARD = 'twineApp/jobTracker/FETCH_BOARD';
const FETCH_BOARD_SUCCESS = 'twineApp/jobTracker/FETCH_BOARD_SUCCESS';
const FETCH_BOARD_FAIL = 'twineApp/jobTracker/FETCH_BOARD_FAIL';

const FETCH_STAGE_PAGE = 'twineApp/jobTracker/FETCH_STAGE_PAGE';
const FETCH_STAGE_PAGE_SUCCESS = 'twineApp/jobTracker/FETCH_STAGE_PAGE_SUCCESS';
const FETCH_STAGE_PAGE_FAIL = 'twineApp/jobTracker/FETCH_STAGE_PAGE_FAIL';

const SAVE_JOB = 'twineApp/jobTracker/SAVE_JOB';
const SAVE_JOB_SUCCESS = 'twineApp/jobTracker/SAVE_JOB_SUCCESS';
const SAVE_JOB_FAIL = 'twineApp/jobTracker/SAVE_JOB_FAIL';

const REMOVE_ENTRY = 'twineApp/jobTracker/REMOVE_ENTRY';
const REMOVE_ENTRY_SUCCESS = 'twineApp/jobTracker/REMOVE_ENTRY_SUCCESS';
const REMOVE_ENTRY_FAIL = 'twineApp/jobTracker/REMOVE_ENTRY_FAIL';

const MOVE_ENTRY = 'twineApp/jobTracker/MOVE_ENTRY';
const MOVE_ENTRY_SUCCESS = 'twineApp/jobTracker/MOVE_ENTRY_SUCCESS';
const MOVE_ENTRY_FAIL = 'twineApp/jobTracker/MOVE_ENTRY_FAIL';

const EDIT_ENTRY = 'twineApp/jobTracker/EDIT_ENTRY';
const EDIT_ENTRY_SUCCESS = 'twineApp/jobTracker/EDIT_ENTRY_SUCCESS';
const EDIT_ENTRY_FAIL = 'twineApp/jobTracker/EDIT_ENTRY_FAIL';

const FETCH_RECENTLY_VIEWED = 'twineApp/jobTracker/FETCH_RECENTLY_VIEWED';
const FETCH_RECENTLY_VIEWED_SUCCESS =
  'twineApp/jobTracker/FETCH_RECENTLY_VIEWED_SUCCESS';
const FETCH_RECENTLY_VIEWED_FAIL =
  'twineApp/jobTracker/FETCH_RECENTLY_VIEWED_FAIL';

const CLEAR_RECENTLY_VIEWED = 'twineApp/jobTracker/CLEAR_RECENTLY_VIEWED';
const CLEAR_RECENTLY_VIEWED_SUCCESS =
  'twineApp/jobTracker/CLEAR_RECENTLY_VIEWED_SUCCESS';
const CLEAR_RECENTLY_VIEWED_FAIL =
  'twineApp/jobTracker/CLEAR_RECENTLY_VIEWED_FAIL';

const FETCH_LINK_PREVIEW = 'twineApp/jobTracker/FETCH_LINK_PREVIEW';
const FETCH_LINK_PREVIEW_SUCCESS =
  'twineApp/jobTracker/FETCH_LINK_PREVIEW_SUCCESS';
const FETCH_LINK_PREVIEW_FAIL = 'twineApp/jobTracker/FETCH_LINK_PREVIEW_FAIL';

const ADD_JOB_BY_URL = 'twineApp/jobTracker/ADD_JOB_BY_URL';
const ADD_JOB_BY_URL_SUCCESS = 'twineApp/jobTracker/ADD_JOB_BY_URL_SUCCESS';
const ADD_JOB_BY_URL_FAIL = 'twineApp/jobTracker/ADD_JOB_BY_URL_FAIL';

const SET_FILTER = 'twineApp/jobTracker/SET_FILTER';
const SET_ACTIVE_STAGE = 'twineApp/jobTracker/SET_ACTIVE_STAGE';
const OPEN_MOVE_SHEET = 'twineApp/jobTracker/OPEN_MOVE_SHEET';
const CLOSE_MOVE_SHEET = 'twineApp/jobTracker/CLOSE_MOVE_SHEET';
const OPEN_ADD_JOB = 'twineApp/jobTracker/OPEN_ADD_JOB';
const CLOSE_ADD_JOB = 'twineApp/jobTracker/CLOSE_ADD_JOB';
const TOGGLE_RECENTLY_VIEWED = 'twineApp/jobTracker/TOGGLE_RECENTLY_VIEWED';
const SET_RECENTLY_VIEWED_OPEN = 'twineApp/jobTracker/SET_RECENTLY_VIEWED_OPEN';
const OPEN_EDIT_ENTRY = 'twineApp/jobTracker/OPEN_EDIT_ENTRY';
const CLOSE_EDIT_ENTRY = 'twineApp/jobTracker/CLOSE_EDIT_ENTRY';
const REQUEST_REMOVE = 'twineApp/jobTracker/REQUEST_REMOVE';
const CANCEL_REMOVE = 'twineApp/jobTracker/CANCEL_REMOVE';
const HIGHLIGHT_ENTRY = 'twineApp/jobTracker/HIGHLIGHT_ENTRY';
const CLEAR_HIGHLIGHT = 'twineApp/jobTracker/CLEAR_HIGHLIGHT';

function normalisePagination(metaPagination) {
  const pagination = emptyPagination();
  if (!metaPagination || typeof metaPagination !== 'object') {
    return pagination;
  }

  STAGES.forEach((stage) => {
    const raw = metaPagination[stage];
    if (!raw) return;
    pagination[stage] = {
      hasMore: Boolean(raw.has_more),
      nextCursor: raw.next_cursor || null,
      status: 'idle',
    };
  });

  return pagination;
}

export function normaliseBoard(payload) {
  const list = (payload && payload.data) || [];
  const entries = {};
  const order = emptyOrder();

  list.forEach((entry) => {
    if (!entry || entry.id == null) return;
    const id = String(entry.id);
    entries[id] = { ...entry, id };
    if (order[entry.stage]) {
      order[entry.stage].push(id);
    }
  });

  const counts =
    payload && payload.meta && payload.meta.counts
      ? { ...emptyCounts(), ...payload.meta.counts }
      : emptyCounts();

  const pagination = normalisePagination(
    payload && payload.meta && payload.meta.pagination
  );

  return { entries, order, counts, pagination };
}

function appendStagePage(state, stage, payload) {
  if (!STAGES.includes(stage)) {
    return state;
  }

  const list = (payload && payload.data) || [];
  const entries = { ...state.entries };
  const order = cloneOrder(state.order);
  const seen = new Set(order[stage]);

  list.forEach((entry) => {
    if (!entry || entry.id == null) return;
    const id = String(entry.id);
    entries[id] = { ...entry, id };
    STAGES.forEach((otherStage) => {
      if (otherStage === stage) return;
      order[otherStage] = order[otherStage].filter((itemId) => itemId !== id);
    });
    if (!seen.has(id)) {
      order[stage].push(id);
      seen.add(id);
    }
  });

  const meta = (payload && payload.meta) || {};
  const pagination = {
    ...state.pagination,
    [stage]: {
      hasMore: Boolean(meta.has_more),
      nextCursor: meta.next_cursor || null,
      status: 'idle',
    },
  };

  return {
    ...state,
    entries,
    order,
    pagination,
  };
}

function cloneOrder(order) {
  return STAGES.reduce((next, stage) => {
    next[stage] = [...(order[stage] || [])];
    return next;
  }, emptyOrder());
}

function insertEntryIntoState(state, entry) {
  const id = String(entry.id);
  const stage = entry.stage;
  const order = cloneOrder(state.order);
  const withoutId = STAGES.reduce((next, key) => {
    next[key] = order[key].filter((itemId) => itemId !== id);
    return next;
  }, emptyOrder());

  if (withoutId[stage]) {
    withoutId[stage] = [id, ...withoutId[stage]];
  }

  const wasPresent = Boolean(state.entries[id]);
  const previousStage = wasPresent ? state.entries[id].stage : null;
  const counts = { ...state.counts };

  if (!wasPresent && counts[stage] !== undefined) {
    counts[stage] += 1;
  } else if (wasPresent && previousStage !== stage) {
    if (counts[previousStage] !== undefined) {
      counts[previousStage] = Math.max(0, counts[previousStage] - 1);
    }
    if (counts[stage] !== undefined) {
      counts[stage] += 1;
    }
  }

  return {
    ...state,
    entries: {
      ...state.entries,
      [id]: { ...entry, id },
    },
    order: withoutId,
    counts,
  };
}

function applyOptimisticMove(state, entryId, toStage) {
  const id = String(entryId);
  const entry = state.entries[id];
  if (!entry || entry.stage === toStage || !state.order[toStage]) {
    return state;
  }

  const fromStage = entry.stage;
  const order = cloneOrder(state.order);
  order[fromStage] = order[fromStage].filter((itemId) => itemId !== id);
  order[toStage] = [id, ...order[toStage].filter((itemId) => itemId !== id)];

  const counts = { ...state.counts };
  if (counts[fromStage] !== undefined) {
    counts[fromStage] = Math.max(0, counts[fromStage] - 1);
  }
  if (counts[toStage] !== undefined) {
    counts[toStage] += 1;
  }

  return {
    ...state,
    entries: {
      ...state.entries,
      [id]: {
        ...entry,
        stage: toStage,
      },
    },
    order,
    counts,
  };
}

function matchesRecentlyViewedBody(item, body) {
  if (!item || !body) return false;
  if (body.job_id != null && item.job_id != null) {
    return String(item.job_id) === String(body.job_id);
  }
  if (body.ari_job_id != null && item.ari_job_id != null) {
    return String(item.ari_job_id) === String(body.ari_job_id);
  }
  return false;
}

function removeRecentlyViewedMatching(items, body) {
  return (items || []).filter((item) => !matchesRecentlyViewedBody(item, body));
}

function removeEntryFromState(state, entryId) {
  const id = String(entryId);
  const entry = state.entries[id];
  if (!entry) {
    return {
      ...state,
      removeEntry: { entryId: null },
    };
  }

  const entries = { ...state.entries };
  delete entries[id];
  const order = cloneOrder(state.order);
  STAGES.forEach((stage) => {
    order[stage] = order[stage].filter((itemId) => itemId !== id);
  });

  const counts = { ...state.counts };
  if (counts[entry.stage] !== undefined) {
    counts[entry.stage] = Math.max(0, counts[entry.stage] - 1);
  }

  return {
    ...state,
    entries,
    order,
    counts,
    removeEntry: { entryId: null },
    editEntry:
      state.editEntry.entryId === id ? { entryId: null } : state.editEntry,
    mobile:
      state.mobile.moveSheetEntryId === id
        ? { ...state.mobile, moveSheetEntryId: null }
        : state.mobile,
  };
}

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case FETCH_BOARD:
      return {
        ...state,
        status: 'loading',
        error: null,
      };

    case FETCH_BOARD_SUCCESS: {
      const normalised = normaliseBoard(action.result);
      return {
        ...state,
        ...normalised,
        status: 'ready',
        error: null,
      };
    }

    case FETCH_BOARD_FAIL:
      return {
        ...state,
        status: 'error',
        error: action.error || true,
      };

    case FETCH_STAGE_PAGE: {
      const stage = action.stage;
      if (!STAGES.includes(stage)) {
        return state;
      }
      return {
        ...state,
        pagination: {
          ...state.pagination,
          [stage]: {
            ...state.pagination[stage],
            status: 'loading',
          },
        },
      };
    }

    case FETCH_STAGE_PAGE_SUCCESS:
      return appendStagePage(state, action.stage, action.result);

    case FETCH_STAGE_PAGE_FAIL: {
      const stage = action.stage;
      if (!STAGES.includes(stage)) {
        return state;
      }
      return {
        ...state,
        pagination: {
          ...state.pagination,
          [stage]: {
            ...state.pagination[stage],
            status: 'error',
          },
        },
      };
    }

    case SAVE_JOB:
      return {
        ...state,
        recentlyViewed: {
          ...state.recentlyViewed,
          items: removeRecentlyViewedMatching(
            state.recentlyViewed.items,
            action.body
          ),
        },
      };

    case SAVE_JOB_SUCCESS:
      return insertEntryIntoState(state, action.result);

    case SAVE_JOB_FAIL:
      return {
        ...state,
        recentlyViewed: {
          ...state.recentlyViewed,
          items: Array.isArray(action.previousRecentlyViewedItems)
            ? action.previousRecentlyViewedItems
            : state.recentlyViewed.items,
        },
        error: action.error || true,
      };

    case REMOVE_ENTRY:
      return state;

    case REMOVE_ENTRY_SUCCESS:
      return removeEntryFromState(state, action.entryId);

    case REMOVE_ENTRY_FAIL:
      return {
        ...state,
        error: action.error || true,
      };

    case MOVE_ENTRY:
      return applyOptimisticMove(state, action.entryId, action.toStage);

    case MOVE_ENTRY_SUCCESS: {
      const serverEntry = action.result;
      if (!serverEntry || serverEntry.id == null) {
        return state;
      }
      const id = String(serverEntry.id);
      return {
        ...state,
        entries: {
          ...state.entries,
          [id]: { ...serverEntry, id },
        },
      };
    }

    case MOVE_ENTRY_FAIL:
      return {
        ...state,
        entries: action.previousEntry
          ? {
              ...state.entries,
              [String(action.entryId)]: action.previousEntry,
            }
          : state.entries,
        order: action.previousOrder
          ? cloneOrder(action.previousOrder)
          : state.order,
        counts: action.previousCounts
          ? { ...action.previousCounts }
          : state.counts,
        error: action.error || true,
      };

    case EDIT_ENTRY:
      return state;

    case EDIT_ENTRY_SUCCESS: {
      const serverEntry = action.result;
      if (!serverEntry || serverEntry.id == null) {
        return state;
      }
      const id = String(serverEntry.id);
      return {
        ...state,
        entries: {
          ...state.entries,
          [id]: { ...serverEntry, id },
        },
        editEntry: { entryId: null },
      };
    }

    case EDIT_ENTRY_FAIL:
      return {
        ...state,
        error: action.error || true,
      };

    case FETCH_RECENTLY_VIEWED:
      return {
        ...state,
        recentlyViewed: {
          ...state.recentlyViewed,
          status: 'loading',
        },
      };

    case FETCH_RECENTLY_VIEWED_SUCCESS:
      return {
        ...state,
        recentlyViewed: {
          ...state.recentlyViewed,
          status: 'ready',
          items: (action.result && action.result.data) || [],
        },
      };

    case FETCH_RECENTLY_VIEWED_FAIL:
      return {
        ...state,
        recentlyViewed: {
          ...state.recentlyViewed,
          status: 'idle',
        },
      };

    case CLEAR_RECENTLY_VIEWED:
      return {
        ...state,
        recentlyViewed: {
          ...state.recentlyViewed,
          items: [],
          status: 'ready',
        },
      };

    case CLEAR_RECENTLY_VIEWED_SUCCESS:
      return {
        ...state,
        recentlyViewed: {
          ...state.recentlyViewed,
          items: [],
          status: 'ready',
        },
      };

    case CLEAR_RECENTLY_VIEWED_FAIL:
      return {
        ...state,
        recentlyViewed: {
          ...state.recentlyViewed,
          items: Array.isArray(action.previousRecentlyViewedItems)
            ? action.previousRecentlyViewedItems
            : state.recentlyViewed.items,
          status: 'ready',
        },
      };

    case FETCH_LINK_PREVIEW:
      return {
        ...state,
        addJob: {
          ...state.addJob,
          preview: null,
          previewStatus: 'loading',
          previewRequestId: action.requestId,
        },
      };

    case FETCH_LINK_PREVIEW_SUCCESS: {
      if (
        action.requestId != null &&
        action.requestId !== state.addJob.previewRequestId
      ) {
        return state;
      }
      return {
        ...state,
        addJob: {
          ...state.addJob,
          preview: action.result,
          previewStatus: 'ready',
        },
      };
    }

    case FETCH_LINK_PREVIEW_FAIL: {
      if (
        action.requestId != null &&
        action.requestId !== state.addJob.previewRequestId
      ) {
        return state;
      }
      return {
        ...state,
        addJob: {
          ...state.addJob,
          preview: null,
          previewStatus: 'failed',
        },
      };
    }

    case ADD_JOB_BY_URL:
      return {
        ...state,
        addJob: {
          ...state.addJob,
          submitting: true,
        },
      };

    case ADD_JOB_BY_URL_SUCCESS: {
      const entry = action.result;
      if (!entry || entry.id == null) {
        return {
          ...state,
          addJob: {
            open: false,
            preview: null,
            previewStatus: 'idle',
            previewRequestId: state.addJob.previewRequestId + 1,
            submitting: false,
          },
        };
      }

      const id = String(entry.id);
      const existed = Boolean(state.entries[id]);
      const nextState = existed ? state : insertEntryIntoState(state, entry);

      return {
        ...nextState,
        addJob: {
          open: false,
          preview: null,
          previewStatus: 'idle',
          previewRequestId: state.addJob.previewRequestId + 1,
          submitting: false,
        },
      };
    }

    case ADD_JOB_BY_URL_FAIL:
      return {
        ...state,
        addJob: {
          ...state.addJob,
          submitting: false,
        },
        error: action.error || true,
      };

    case SET_FILTER:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.filters,
        },
      };

    case SET_ACTIVE_STAGE:
      return {
        ...state,
        mobile: {
          ...state.mobile,
          activeStage: action.stage,
        },
      };

    case OPEN_MOVE_SHEET:
      return {
        ...state,
        mobile: {
          ...state.mobile,
          moveSheetEntryId: action.entryId,
        },
      };

    case CLOSE_MOVE_SHEET:
      return {
        ...state,
        mobile: {
          ...state.mobile,
          moveSheetEntryId: null,
        },
      };

    case OPEN_ADD_JOB:
      return {
        ...state,
        addJob: {
          ...state.addJob,
          open: true,
        },
      };

    case CLOSE_ADD_JOB:
      return {
        ...state,
        addJob: {
          open: false,
          preview: null,
          previewStatus: 'idle',
          previewRequestId: state.addJob.previewRequestId + 1,
          submitting: false,
        },
      };

    case HIGHLIGHT_ENTRY:
      return {
        ...state,
        highlightedEntryId: action.entryId ? String(action.entryId) : null,
      };

    case CLEAR_HIGHLIGHT:
      if (
        action.entryId &&
        state.highlightedEntryId &&
        String(action.entryId) !== String(state.highlightedEntryId)
      ) {
        return state;
      }
      return {
        ...state,
        highlightedEntryId: null,
      };

    case TOGGLE_RECENTLY_VIEWED:
      return {
        ...state,
        recentlyViewed: {
          ...state.recentlyViewed,
          open: !state.recentlyViewed.open,
        },
      };

    case SET_RECENTLY_VIEWED_OPEN:
      return {
        ...state,
        recentlyViewed: {
          ...state.recentlyViewed,
          open: Boolean(action.open),
        },
      };

    case OPEN_EDIT_ENTRY:
      return {
        ...state,
        editEntry: {
          entryId: action.entryId,
        },
      };

    case CLOSE_EDIT_ENTRY:
      return {
        ...state,
        editEntry: {
          entryId: null,
        },
      };

    case REQUEST_REMOVE:
      return {
        ...state,
        removeEntry: {
          entryId: action.entryId,
        },
      };

    case CANCEL_REMOVE:
      return {
        ...state,
        removeEntry: {
          entryId: null,
        },
      };

    default:
      return state;
  }
}

export function fetchBoard(options = {}) {
  return {
    types: [FETCH_BOARD, FETCH_BOARD_SUCCESS, FETCH_BOARD_FAIL],
    promise: (client) => getBoard(client, options),
  };
}

/**
 * Load the next page for a stage when hasMore and not already loading.
 */
export function fetchMoreForStage(stage) {
  return (dispatch, getState) => {
    if (!STAGES.includes(stage)) {
      return Promise.resolve();
    }

    const { pagination } = getState().jobTracker;
    const page = pagination[stage] || {};
    if (!page.hasMore || page.status === 'loading' || !page.nextCursor) {
      return Promise.resolve();
    }

    return dispatch({
      types: [
        FETCH_STAGE_PAGE,
        FETCH_STAGE_PAGE_SUCCESS,
        FETCH_STAGE_PAGE_FAIL,
      ],
      stage,
      promise: (client) =>
        getStagePage(client, stage, { cursor: page.nextCursor }),
    });
  };
}

export function saveJob(body) {
  return (dispatch, getState) => {
    const previousRecentlyViewedItems = (
      getState().jobTracker.recentlyViewed.items || []
    ).map((item) => ({ ...item }));

    return dispatch({
      types: [SAVE_JOB, SAVE_JOB_SUCCESS, SAVE_JOB_FAIL],
      body,
      previousRecentlyViewedItems,
      promise: (client) => createEntry(client, body),
    });
  };
}

export function removeEntry(entryId) {
  return {
    types: [REMOVE_ENTRY, REMOVE_ENTRY_SUCCESS, REMOVE_ENTRY_FAIL],
    entryId,
    promise: (client) => deleteEntry(client, entryId),
  };
}

/**
 * Find a pipeline entry id in board state by Twine job id or Ari job id.
 * @param {object} state
 * @param {{ jobId?: number|string|null, ariJobId?: number|string|null }} refs
 * @returns {string|number|null}
 */
export function findEntryIdByJobRef(state, { jobId = null, ariJobId = null } = {}) {
  const entries = (state && state.jobTracker && state.jobTracker.entries) || {};
  const ids = Object.keys(entries);
  for (let i = 0; i < ids.length; i += 1) {
    const entry = entries[ids[i]];
    if (!entry) continue;
    if (
      jobId != null &&
      entry.job_id != null &&
      String(entry.job_id) === String(jobId)
    ) {
      return entry.id;
    }
    if (
      ariJobId != null &&
      entry.ari_job_id != null &&
      String(entry.ari_job_id) === String(ariJobId)
    ) {
      return entry.id;
    }
  }
  return null;
}

/**
 * Remove a pipeline entry by id, or by job_id / ari_job_id lookup in board state.
 * @param {{ entryId?: number|string|null, jobId?: number|string|null, ariJobId?: number|string|null }} refs
 */
export function removeEntryForJob({
  entryId = null,
  jobId = null,
  ariJobId = null,
} = {}) {
  return (dispatch, getState) => {
    const id =
      entryId != null
        ? entryId
        : findEntryIdByJobRef(getState(), { jobId, ariJobId });
    if (id == null) {
      return Promise.reject({ error: 'missing_entry_id' });
    }
    return dispatch(removeEntry(id));
  };
}

export function moveEntry(entryId, stage) {
  return (dispatch, getState) => {
    const state = getState().jobTracker;
    const id = String(entryId);
    const entry = state.entries[id];
    if (!entry || entry.stage === stage) {
      return Promise.resolve();
    }

    return dispatch({
      types: [MOVE_ENTRY, MOVE_ENTRY_SUCCESS, MOVE_ENTRY_FAIL],
      entryId: id,
      fromStage: entry.stage,
      toStage: stage,
      previousEntry: { ...entry },
      previousOrder: cloneOrder(state.order),
      previousCounts: { ...state.counts },
      promise: (client) => updateEntryStage(client, id, stage),
    });
  };
}

export function editEntry(entryId, body) {
  return {
    types: [EDIT_ENTRY, EDIT_ENTRY_SUCCESS, EDIT_ENTRY_FAIL],
    entryId,
    promise: (client) => updateEntry(client, entryId, body),
  };
}

export function fetchRecentlyViewed() {
  return {
    types: [
      FETCH_RECENTLY_VIEWED,
      FETCH_RECENTLY_VIEWED_SUCCESS,
      FETCH_RECENTLY_VIEWED_FAIL,
    ],
    promise: (client) => getRecentlyViewed(client),
  };
}

export function clearRecentlyViewed() {
  return (dispatch, getState) => {
    const previousRecentlyViewedItems = (
      getState().jobTracker.recentlyViewed.items || []
    ).map((item) => ({ ...item }));

    return dispatch({
      types: [
        CLEAR_RECENTLY_VIEWED,
        CLEAR_RECENTLY_VIEWED_SUCCESS,
        CLEAR_RECENTLY_VIEWED_FAIL,
      ],
      previousRecentlyViewedItems,
      promise: (client) => clearRecentlyViewedApi(client),
    });
  };
}

const LOG_RECENTLY_VIEWED = 'twineApp/jobTracker/LOG_RECENTLY_VIEWED';
const LOG_RECENTLY_VIEWED_SUCCESS =
  'twineApp/jobTracker/LOG_RECENTLY_VIEWED_SUCCESS';
const LOG_RECENTLY_VIEWED_FAIL = 'twineApp/jobTracker/LOG_RECENTLY_VIEWED_FAIL';

/**
 * Fire-and-forget POST /job-tracker/recently-viewed for Ari external click-outs.
 * Twine job detail views are logged server-side — do not call for those.
 * @param {number|string} ariJobId ExternalJob id (numeric)
 */
export function logAriRecentlyViewed(ariJobId) {
  if (ariJobId == null || ariJobId === '') {
    return { type: LOG_RECENTLY_VIEWED_FAIL };
  }
  const id = Number(ariJobId);
  if (!Number.isFinite(id) || id <= 0) {
    return { type: LOG_RECENTLY_VIEWED_FAIL };
  }
  return {
    types: [
      LOG_RECENTLY_VIEWED,
      LOG_RECENTLY_VIEWED_SUCCESS,
      LOG_RECENTLY_VIEWED_FAIL,
    ],
    promise: (client) =>
      postRecentlyViewed(client, { ari_job_id: id }).catch(() => null),
  };
}

export function fetchLinkPreview(url) {
  return (dispatch, getState) => {
    const requestId = (getState().jobTracker.addJob.previewRequestId || 0) + 1;
    return dispatch({
      types: [
        FETCH_LINK_PREVIEW,
        FETCH_LINK_PREVIEW_SUCCESS,
        FETCH_LINK_PREVIEW_FAIL,
      ],
      requestId,
      promise: (client) => fetchLinkPreviewApi(client, url),
    });
  };
}

/**
 * @param {object} body
 * @param {string} body.url
 * @param {string} body.title
 * @param {string} [body.company]
 * @param {string} [body.location]
 * @param {string} [body.source_site]
 * @param {string} body.stage
 * @param {boolean} [body.fetched] Whether link preview supplied the details
 */
export function addJobByUrl(body) {
  return (dispatch, getState) => {
    const beforeEntries = getState().jobTracker.entries;
    const { fetched, ...payload } = body || {};

    return dispatch({
      types: [ADD_JOB_BY_URL, ADD_JOB_BY_URL_SUCCESS, ADD_JOB_BY_URL_FAIL],
      promise: (client) => createEntry(client, payload),
    }).then(
      (result) => {
        const id = result && result.id != null ? String(result.id) : null;
        const wasDuplicate = Boolean(id && beforeEntries[id]);

        if (wasDuplicate) {
          dispatch(setSuccessMessage("That job's already on your board."));
        } else {
          trackEvent('job-tracker-job-added-by-url', {
            fetched: Boolean(fetched),
            stage: payload.stage,
          });
          trackEvent('job-tracker-job-saved', {
            source: 'manual',
            entry_point: 'add_job',
          });
        }

        if (id) {
          dispatch(highlightEntry(id));
        }

        return result;
      },
      (error) => {
        dispatch(setErrorMessage("We couldn't add that job. Try again."));
        throw error;
      }
    );
  };
}

export function highlightEntry(entryId) {
  return (dispatch) => {
    const id = entryId != null ? String(entryId) : null;
    dispatch({ type: HIGHLIGHT_ENTRY, entryId: id });
    if (!id || typeof setTimeout === 'undefined') {
      return;
    }
    setTimeout(() => {
      dispatch({ type: CLEAR_HIGHLIGHT, entryId: id });
    }, HIGHLIGHT_MS);
  };
}

export function setFilter(filters) {
  return { type: SET_FILTER, filters };
}

export function setActiveStage(stage) {
  return { type: SET_ACTIVE_STAGE, stage };
}

export function openMoveSheet(entryId) {
  return { type: OPEN_MOVE_SHEET, entryId };
}

export function closeMoveSheet() {
  return { type: CLOSE_MOVE_SHEET };
}

export function openAddJob() {
  return { type: OPEN_ADD_JOB };
}

export function closeAddJob() {
  return { type: CLOSE_ADD_JOB };
}

export function toggleRecentlyViewed() {
  return { type: TOGGLE_RECENTLY_VIEWED };
}

export function setRecentlyViewedOpen(open) {
  return { type: SET_RECENTLY_VIEWED_OPEN, open: Boolean(open) };
}

export function openEditEntry(entryId) {
  return { type: OPEN_EDIT_ENTRY, entryId };
}

export function closeEditEntry() {
  return { type: CLOSE_EDIT_ENTRY };
}

export function requestRemove(entryId) {
  return { type: REQUEST_REMOVE, entryId };
}

export function cancelRemove() {
  return { type: CANCEL_REMOVE };
}

export function selectBoardStatus(state) {
  return state.jobTracker.status;
}

export function selectCounts(state) {
  return state.jobTracker.counts;
}

export function selectStagePagination(state, stage) {
  const pagination = state.jobTracker.pagination || {};
  return (
    pagination[stage] || {
      hasMore: false,
      nextCursor: null,
      status: 'idle',
    }
  );
}

export function selectEntriesForStage(state, stage) {
  const { entries, order, filters } = state.jobTracker;
  const ids = (order && order[stage]) || [];
  const query = (filters.query || '').trim().toLowerCase();

  return ids
    .map((id) => entries[id])
    .filter(Boolean)
    .filter((entry) => {
      if (filters.source === 'twine' && entry.source !== 'twine') {
        return false;
      }
      if (
        filters.source === 'external' &&
        entry.source !== 'ari' &&
        entry.source !== 'manual'
      ) {
        return false;
      }
      if (!query) {
        return true;
      }
      const title = (entry.title || '').toLowerCase();
      const company = (entry.company || '').toLowerCase();
      return title.includes(query) || company.includes(query);
    });
}
